<template>
  <div
    class="mission-ranking-table-item-parts"
    :class="`mission-ranking-table-item-parts--${rankingType.toLowerCase()}`"
  >
    <div
      class="mission-ranking-table-item-parts__container"
      :class="{ 'mission-ranking-table-item-parts__container--self': isOwnRank }"
    >
      <div class="mission-ranking-table-item-parts__main">
        <p
          class="mission-ranking-table-item-parts__rank"
          :class="`mission-ranking-table-item-parts__rank--${userData.rank}`"
        >
          <span class="mission-ranking-table-item-parts__rank-number">{{ userData.rank }}</span>
        </p>
        <div class="mission-ranking-table-item-parts__user">
          <div class="mission-ranking-table-item-parts__user-avatar">
            <img
              class="mission-ranking-table-item-parts__user-avatar-image"
              :src="getUserIcon"
              alt=""
            />
          </div>
          <p class="mission-ranking-table-item-parts__user-name">{{ getUserName }}</p>
        </div>
        <p class="mission-ranking-table-item-parts__point">
          <span class="mission-ranking-table-item-parts__point-data">{{ userData.point }}</span>
          <span class="mission-ranking-table-item-parts__point-unit">P</span>
        </p>
      </div>
      <div class="mission-ranking-table-item-parts__share" v-if="isOwnRank">
        <div class="mission-ranking-table-item-parts__share-inner">
          <mission-ranking-table-share-parts
            @savePostSnsMissionHistory="$emit('savePostSnsMissionHistory')"
            @onClickSnsButton="onClickSnsButton"
            @onClickShareIcon="onClickShareIcon"
          />
        </div>
        <div class="mission-ranking-table-item-parts__share-bg"></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import MissionRankingTableShareParts from '@/components/MissionPage/MissionRankingPane/parts/MissionRankingTableShareParts.vue'
import { RankInUserType } from '@/store/stores/collectionModule/documents/pointRanking/PointRankingDataDocument'
import { UserRetrieveNameResponseType } from '@/store/stores/collectionModule/documents/user/UserRetrieveNameDocument'
import CloudFrontUtil from '@/util/aws/CloudFrontUtil'
import { RankingType } from '@/store/stores/pageStore/MissionPage/MissionPageStore'
import useMissionRanking from '@/components/MissionPage/MissionRankingPane/hook/useMissionRanking'

/* eslint-disable @typescript-eslint/no-var-requires */
const defaultIcon = require('@/assets/img/icon/icon_user_default.png')

export default defineComponent({
  name: 'MissionRankingTableItemParts',
  components: { MissionRankingTableShareParts },
  props: {
    userData: {
      type: Object as PropType<RankInUserType>,
      required: true,
    },
    isOwnRank: {
      type: Boolean,
      default: false,
    },
    retrieveNameUsersByUserId: {
      type: Object as PropType<Record<string, UserRetrieveNameResponseType>>,
    },
    rankingType: {
      type: String as PropType<RankingType>,
      default: 'TOTAL',
    },
  },
  setup(props) {
    const { getDisplayUserName } = useMissionRanking()

    const getUserName = computed(() =>
      getDisplayUserName(props.retrieveNameUsersByUserId, props.userData),
    )

    const getUserIcon = computed(() => {
      const userIcon = props.retrieveNameUsersByUserId?.[props.userData.userId]?.userPicture
      return userIcon ? CloudFrontUtil.getSignedUrl(userIcon) : defaultIcon
    })

    return {
      getUserName,
      getUserIcon,
    }
  },
  methods: {
    onClickSnsButton() {
      this.$emit('onClickSnsButton')
    },
    onClickShareIcon() {
      this.$emit('onClickShareIcon')
    },
  },
})
</script>

<style scoped lang="scss">
.mission-ranking-table-item-parts {
  &__container {
    margin-right: 3px;
    margin-left: 3px;

    &--self {
      overflow: hidden;
      background-color: rgba($green-light, 0.5);
      border: 1px solid rgba($white, 0.3);
      border-radius: 6px;

      .mission-ranking-table-item-parts--season & {
        background-color: rgba(#ebe553, 0.2);
      }

      .mission-ranking-table-item-parts--monthly & {
        background-color: rgba(#ff5d5d, 0.36);
      }
    }
  }

  &__main {
    position: relative;
    display: flex;
    align-items: center;
    height: 55px;
    padding: 10px 9px;
    color: $white;
  }

  &__rank {
    position: relative;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 30px;
    margin-right: 18px;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.02em;

    .mission-ranking-table-item-parts__container--self & {
      width: auto;
      min-width: 40px;
    }

    &-number {
      position: relative;
      z-index: 3;
    }

    &--1,
    &--2,
    &--3 {
      color: $green-light;

      .mission-ranking-table-item-parts--season & {
        color: #ebe553;
      }

      .mission-ranking-table-item-parts--monthly & {
        color: #ff5d5d;
      }

      .mission-ranking-table-item-parts__container--self & {
        color: $white;
      }

      &::before {
        position: absolute;
        top: 50%;
        left: 50%;
        z-index: 2;
        width: 56px;
        height: 53px;
        content: '';
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
        transform: translate(-50%, -54%);
      }

      &::after {
        position: absolute;
        top: 50%;
        left: 50%;
        z-index: 1;
        width: 67px;
        height: 70px;
        content: '';
        background-image: url('@/assets/img/MissionPage/ranking/bg_ranking_black.svg');
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
        transform: translate(-50%, -50%);
      }
    }

    &--1 {
      &::before {
        background-image: url('@/assets/img/MissionPage/ranking/bg_ranking_first.png');
      }
    }

    &--2 {
      &::before {
        background-image: url('@/assets/img/MissionPage/ranking/bg_ranking_second.png');
      }
    }

    &--3 {
      &::before {
        background-image: url('@/assets/img/MissionPage/ranking/bg_ranking_third.png');
      }
    }
  }

  &__user {
    display: flex;
    gap: 12px;
    flex-grow: 1;
    align-items: center;
    overflow: hidden;

    &-avatar {
      flex-shrink: 0;
      width: 32px;
      height: 32px;
      overflow: hidden;
      border-radius: 50%;

      &-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    &-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__point {
    flex-shrink: 0;
    min-width: 80px;
    margin-right: 5px;
    text-align: right;
  }

  &__share {
    position: relative;
    height: 55px;
    padding: 10px 8px;

    &-inner {
      position: relative;
      z-index: 1;
    }

    &-bg {
      position: absolute;
      top: 0;
      left: 15px;
      width: calc(100% - 30px);
      height: 55px;
      background-image: url('@/assets/img/MissionPage/ranking/bg_share_center.png');
      background-repeat: repeat-x;
      background-position: left top;
      background-size: 300px 55px;
    }

    &::before {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 15px;
      height: 71px;
      content: '';
      background-image: url('@/assets/img/MissionPage/ranking/bg_share_left.png');
      background-repeat: no-repeat;
      background-position: left bottom;
      background-size: contain;
    }

    &::after {
      position: absolute;
      bottom: 0;
      right: 0;
      width: 15px;
      height: 55px;
      content: '';
      background-image: url('@/assets/img/MissionPage/ranking/bg_share_right.png');
      background-repeat: no-repeat;
      background-position: right top;
      background-size: contain;
    }
  }
}
</style>
